// Shared chat-media helpers for the agent dock: attachment classification, presigned S3
// upload, byte formatting, and the curated emoji set used by the lightweight picker.
//
// Uploads reuse the same presigned-PUT flow as product images (getPresignedUrl -> PUT ->
// strip the query string for the public URL). Only the resulting public URL + metadata are
// sent over the socket — never the binary.

import axios from "axios";
import { v4 as uuid } from "uuid";
import { PRESIGNED_URL } from "../../../constants/config";

// Mirror server constants/EcomChatMessageType.
export const MESSAGE_TYPE = { TEXT: 1, IMAGE: 2, FILE: 3 };

export const MAX_FILE_BYTES = 10 * 1024 * 1024; // keep in sync with EcomChatAttachment

const IMAGE_MIMES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const FILE_MIMES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/zip",
];

export const FILE_ACCEPT = [...IMAGE_MIMES, ...FILE_MIMES].join(",");

export function typeForMime(mime) {
  if (IMAGE_MIMES.includes(mime)) return MESSAGE_TYPE.IMAGE;
  return MESSAGE_TYPE.FILE;
}

export function isAllowedMime(mime) {
  return IMAGE_MIMES.includes(mime) || FILE_MIMES.includes(mime);
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Upload a File/Blob to S3 via a presigned URL and return the attachment descriptor the
// socket expects. `name` is the original filename (or a generated one for blobs).
export async function uploadChatFile(file, name) {
  const filename = name || file.name || `file-${uuid()}`;
  const key = `media/ecom-chat/${uuid()}/${filename}`.replace(/\s+/g, "-");
  const { data } = await axios.post(PRESIGNED_URL, [{ key }]);
  const putUrl = data[0].url;
  const res = await fetch(putUrl, { method: "PUT", body: file });
  if (!res.ok) throw new Error(`upload failed (${res.status})`);
  return {
    url: putUrl.split("?")[0],
    name: filename,
    mime: file.type || "application/octet-stream",
    size: file.size,
  };
}

// A compact, dependency-free emoji set grouped into a few tabs — enough for chat without
// pulling a heavy picker library into the Webpack 4 build.
export const EMOJI_GROUPS = [
  {
    label: "😀",
    emojis: ["😀", "😁", "😂", "🤣", "😊", "😍", "😘", "😅", "😉", "🙂", "🤔", "😎", "😴", "😢", "😭", "😡", "🥳", "😇", "🤗", "😋"],
  },
  {
    label: "👍",
    emojis: ["👍", "👎", "👏", "🙏", "🙌", "👌", "✌️", "🤝", "💪", "👋", "🤞", "🫶", "👀", "🫡", "🤙"],
  },
  {
    label: "❤️",
    emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🔥", "⭐", "✨", "🎉", "🎁", "💯", "✅", "❌", "⚠️"],
  },
  {
    label: "🛒",
    emojis: ["🛒", "📦", "💳", "💰", "🚚", "🏷️", "🎀", "📍", "📞", "✉️", "🕐", "📝", "🔖", "🛍️"],
  },
];
